import { fetchRoadSnapCandidates, type RoadSnapCandidateDto } from '../../services/api';
import {
  resampleTracePathIfGap,
  type TraceLatLng,
} from '../../utils/tracePostProcess';
import {
  DEFAULT_ROAD_SNAP_BANDS,
  type RoadSnapBandConfig,
} from './roadSnapBands';

/** @deprecated use DEFAULT_ROAD_SNAP_BANDS.autoM */
export const SNAP_AUTO_M = DEFAULT_ROAD_SNAP_BANDS.autoM;
/** @deprecated use DEFAULT_ROAD_SNAP_BANDS.searchM */
export const SNAP_CONFIRM_M = DEFAULT_ROAD_SNAP_BANDS.searchM;
export const POLYGON_CLOSE_MAX_M = 15;

export type SnapConnectionRecord = {
  kind: 'endpoint';
  end: 'start' | 'end';
  source?: 'road' | 'contribution';
  road_id?: number;
  contribution_id?: string;
  endpoint: 'start' | 'end';
  snap_lng: number;
  snap_lat: number;
  /** Pre-snap vertex (visual connector on map). */
  original_lng?: number;
  original_lat?: number;
  distance_m: number;
  mode: 'auto' | 'manual';
};

export type SnapResolve = 'auto' | 'confirm' | 'none';

export function resolveSnapBand(
  distanceM: number,
  eligible: boolean,
  ambiguous: boolean,
  bands: RoadSnapBandConfig = DEFAULT_ROAD_SNAP_BANDS,
): SnapResolve {
  if (!eligible || ambiguous) return distanceM <= bands.searchM ? 'confirm' : 'none';
  if (distanceM <= bands.autoM) return 'auto';
  if (distanceM <= bands.searchM) return 'confirm';
  return 'none';
}

export function isAmbiguousCluster(candidates: RoadSnapCandidateDto[]): boolean {
  const eligible = candidates.filter((c) => c.eligible);
  if (eligible.length < 2) return false;
  for (let i = 0; i < eligible.length; i++) {
    for (let j = i + 1; j < eligible.length; j++) {
      const dx = eligible[i].snap_lng - eligible[j].snap_lng;
      const dy = eligible[i].snap_lat - eligible[j].snap_lat;
      const approxM = Math.hypot(dx, dy) * 111_320;
      if (approxM < 2) return true;
    }
  }
  return false;
}

export function pickBestCandidate(
  candidates: RoadSnapCandidateDto[],
  preferAutoOnly: boolean,
  bands: RoadSnapBandConfig = DEFAULT_ROAD_SNAP_BANDS,
): RoadSnapCandidateDto | null {
  const eligible = candidates.filter((c) => c.eligible);
  if (!eligible.length) return null;
  const ambiguous = isAmbiguousCluster(eligible);
  const sorted = [...eligible].sort((a, b) => a.distance_m - b.distance_m);
  const best = sorted[0];
  const band = resolveSnapBand(best.distance_m, best.eligible, ambiguous, bands);
  if (band === 'none') return null;
  if (preferAutoOnly && band !== 'auto') return null;
  return best;
}

function snapCandidateMessage(best: RoadSnapCandidateDto): string {
  const endLabel = best.endpoint === 'start' ? 'start' : 'end';
  if (best.source === 'contribution' || best.contribution_id) {
    const label = best.name?.trim() || 'your pending survey';
    return `Snapped to ${label} (${endLabel} · pending review)`;
  }
  if (best.name) {
    return `Snapped to ${best.name} (${endLabel} end)`;
  }
  if (best.road_id != null) {
    return `Snapped to road #${best.road_id}`;
  }
  return 'Snapped to nearby endpoint';
}

export async function snapPointAt(
  lng: number,
  lat: number,
  preferAutoOnly: boolean,
  endLabel: 'start' | 'end' = 'start',
  bands: RoadSnapBandConfig = DEFAULT_ROAD_SNAP_BANDS,
  fetchOptions?: { includeMyPending?: boolean; campaignId?: string | null },
): Promise<{ lng: number; lat: number; connection: SnapConnectionRecord | null; message: string | null }> {
  const candidates = await fetchRoadSnapCandidates(lng, lat, bands.searchM, fetchOptions);
  const best = pickBestCandidate(candidates, preferAutoOnly, bands);
  if (!best) {
    return { lng, lat, connection: null, message: null };
  }
  const ambiguous = isAmbiguousCluster(candidates.filter((c) => c.eligible));
  const band = resolveSnapBand(best.distance_m, best.eligible, ambiguous, bands);
  if (band === 'confirm' && preferAutoOnly) {
    return { lng, lat, connection: null, message: 'Choose connection — nearby road end' };
  }
  const isContribution = best.source === 'contribution' || Boolean(best.contribution_id);
  return {
    lng: best.snap_lng,
    lat: best.snap_lat,
    connection: {
      kind: 'endpoint',
      end: endLabel,
      source: isContribution ? 'contribution' : 'road',
      ...(isContribution
        ? { contribution_id: best.contribution_id ?? undefined }
        : { road_id: best.road_id ?? undefined }),
      endpoint: best.endpoint as 'start' | 'end',
      snap_lng: best.snap_lng,
      snap_lat: best.snap_lat,
      original_lng: lng,
      original_lat: lat,
      distance_m: best.distance_m,
      mode: band === 'auto' ? 'auto' : 'manual',
    },
    message: snapCandidateMessage(best),
  };
}

export async function snapLineStringEndpoints(
  geometry: GeoJSON.LineString,
  options?: {
    autoOnly?: boolean;
    bands?: RoadSnapBandConfig;
    includeMyPending?: boolean;
    campaignId?: string | null;
    /** When set, fill long gaps after endpoint snap (uses trace spacing). */
    resampleSpacingM?: number;
  },
): Promise<{ geometry: GeoJSON.LineString; connections: SnapConnectionRecord[]; message: string | null }> {
  const bands = options?.bands ?? DEFAULT_ROAD_SNAP_BANDS;
  const fetchOptions = {
    includeMyPending: options?.includeMyPending,
    campaignId: options?.campaignId,
  };
  const coords = geometry.coordinates as [number, number][];
  if (coords.length < 2) {
    return { geometry, connections: [], message: null };
  }
  const connections: SnapConnectionRecord[] = [];
  const next = coords.map((c) => [...c] as [number, number]);
  const autoOnly = options?.autoOnly ?? true;

  const start = await snapPointAt(coords[0][0], coords[0][1], autoOnly, 'start', bands, fetchOptions);
  if (start.connection) {
    next[0] = [start.lng, start.lat];
    connections.push(start.connection);
  }

  const endIdx = coords.length - 1;
  const end = await snapPointAt(coords[endIdx][0], coords[endIdx][1], autoOnly, 'end', bands, fetchOptions);
  if (end.connection) {
    next[endIdx] = [end.lng, end.lat];
    connections.push(end.connection);
  }

  const parts = [start.message, end.message].filter(Boolean);
  let coordinates = next;
  const spacingM = options?.resampleSpacingM;
  if (spacingM != null && spacingM > 0 && connections.length > 0) {
    const asTrace: TraceLatLng[] = next.map(([lng, lat]) => ({ lat, lng }));
    const resampled = resampleTracePathIfGap(asTrace, spacingM);
    coordinates = resampled.map((p) => [p.lng, p.lat] as [number, number]);
  }

  return {
    geometry: { type: 'LineString', coordinates },
    connections,
    message: parts.length ? parts.join(' · ') : null,
  };
}

export function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** Close polygon ring to first vertex when within tolerance. */
export function tryClosePolygonRing(
  ring: [number, number][],
  maxGapM = POLYGON_CLOSE_MAX_M,
): { ring: [number, number][]; closed: boolean; gapM: number | null } {
  if (ring.length < 3) return { ring, closed: false, gapM: null };
  const first = ring[0];
  const last = ring[ring.length - 1];
  const gapM = haversineM(first[1], first[0], last[1], last[0]);
  if (gapM > maxGapM) return { ring, closed: false, gapM };
  const closed = [...ring.slice(0, -1), first];
  if (closed[0][0] !== closed[closed.length - 1][0] || closed[0][1] !== closed[closed.length - 1][1]) {
    closed.push([...first]);
  }
  return { ring: closed, closed: true, gapM };
}
